// TODO Please remove this file from nimported ignore list at '.unimportedrc.json' when this file already use
import { Card, CardProps, Space } from "antd";
import { TestID, TestIDWrapper } from "../../shared/testids/testids";
import "./CORECard.less";
import classnames from "classnames";
import { CORETooltip } from "../Overlay/CORETooltip";
import React, {
  cloneElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { HOCErrorBoundary } from "../../shared/errors/ErrorBoundary";
import { COREDivider } from "../Layout/COREDivider";
import { CORECarousel, CORECarouselProps } from "./CORECarousel";
import { Link, useNavigate } from "react-router-dom";

interface CommonProps {
  testID: TestID;
  /** Header media content (e.g., CORECarousel component) */
  headerMedia?: ReactNode;
  layout?: "vertical" | "horizontal";
  icon?: React.ReactNode;
  elevation?: "none" | "weak" | "intense";
  actions?: React.ReactElement[];
  lite?: boolean;
  fitContainerHeight?: boolean;
  disabled?: boolean;
  /**
   * When provided, renders an absolutely-positioned <Link> that covers the
   * card body (stretched link pattern). This enables browser right-click
   * navigation (open in new tab) while keeping interactive children
   * (action bars, buttons) fully clickable via z-index elevation.
   * Prefer this over onClick + useNavigate() for navigation cards.
   */
  href?: string;
  /** Link target for the stretched link. Use "_blank" for external URLs. */
  target?: React.HTMLAttributeAnchorTarget;
}

export type CORECardProps = CommonProps & CardProps;

const getInnerCarouselProps = (
  media: ReactNode
): Partial<CORECarouselProps> | null => {
  if (!React.isValidElement(media)) return null;
  if (media.type === CORECarousel) return media.props as CORECarouselProps;
  const children = React.Children.toArray(media.props.children);
  if (children.length === 1) return getInnerCarouselProps(children[0]);
  return null;
};

const addHorizontalMediaClass = (
  media?: ReactNode,
  options?: {
    disabled?: boolean;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
  }
): ReactNode => {
  const { disabled, onClick } = options ?? {};
  if (!React.isValidElement(media)) {
    return media;
  }

  if (media.type === CORECarousel) {
    const hasCustomHeight = media.props.height !== undefined;
    return cloneElement(media, {
      wrapperClassName: classnames(
        "core-card-cover",
        { "core-card-cover--dynamic-height": hasCustomHeight },
        { "core-card-cover--disabled": disabled },
        media.props.wrapperClassName
      ),
      disabled: media.props.disabled ?? disabled,
      onClick: media.props.onClick ?? onClick,
    });
  }

  const innerCarouselProps = getInnerCarouselProps(media);
  const hasCustomHeight = innerCarouselProps?.height !== undefined;

  return (
    <div
      className={classnames("core-card-cover", {
        "core-card-cover--dynamic-height": hasCustomHeight,
        "core-card-cover--disabled": disabled,
      })}
    >
      {media}
    </div>
  );
};

const HorizontalCard: React.FC<
  Pick<CORECardProps, "children" | "headerMedia"> & {
    disabled?: boolean;
    onCarouselImageClick?: React.MouseEventHandler<HTMLDivElement>;
  }
> = ({ children, headerMedia, disabled, onCarouselImageClick }) => (
  <div className={"core-card-horizontal"}>
    <div className={"img-box"}>
      {addHorizontalMediaClass(headerMedia, {
        disabled,
        onClick: onCarouselImageClick,
      })}
    </div>
    <div className={"content-box"}>{children}</div>
  </div>
);

const CORECardNoError: React.FC<CORECardProps> = ({
  testID,
  headerMedia,
  title,
  icon,
  className,
  hoverable = true,
  onClick,
  elevation = "weak",
  layout = "vertical",
  children,
  actions,
  lite = false,
  fitContainerHeight,
  bordered = true,
  disabled = false,
  href,
  target,
  ...prop
}) => {
  const navigate = useNavigate();
  const textElementRef = useRef<HTMLDivElement>(null); // For horizontal
  const [isOverflowed, setIsOverflow] = useState<boolean>(false);
  const isSmall = prop.size === "small";

  const onCarouselImageClick:
    | React.MouseEventHandler<HTMLDivElement>
    | undefined =
    href && !disabled
      ? () => {
          if (target === "_blank") {
            window.open(href, "_blank", "noopener,noreferrer");
          } else {
            navigate(href);
          }
        }
      : undefined;

  let resolvedHeaderMedia = headerMedia;

  if (layout === "vertical" && React.isValidElement(headerMedia)) {
    if (headerMedia.type === CORECarousel) {
      resolvedHeaderMedia = cloneElement(headerMedia, {
        borderRadius:
          layout === "vertical" && bordered
            ? headerMedia.props.borderRadius ?? [8, 8, 0, 0]
            : headerMedia.props.borderRadius,
        disabled: headerMedia.props.disabled ?? disabled,
        onClick: headerMedia.props.onClick ?? onCarouselImageClick,
      });
    } else {
      // Wrapped carousel (e.g. <a> or <Link>): ensure the wrapper fills the
      // card cover area as a block element so it doesn't overlap the body.
      resolvedHeaderMedia = (
        <div className="core-card-cover-wrapper">{headerMedia}</div>
      );
    }
  }

  const compareSize = () => {
    const compare =
      textElementRef.current !== null &&
      textElementRef.current!.scrollWidth > textElementRef.current!.clientWidth;
    setIsOverflow(compare);
  };

  useEffect(() => {
    compareSize();
    window.addEventListener("resize", compareSize);
  }, [title]);

  const cover = resolvedHeaderMedia || prop.cover;
  if (icon) {
    title = (
      <Space size={8}>
        <div>{icon}</div>
        <div>{title}</div>
      </Space>
    );
  }

  const actionsWithStyle = actions?.map((childTree) => {
    return cloneElement(childTree, {
      ...childTree.props,
      className: classnames(
        "core-card-actions",
        { "core-card-actions--disabled": disabled },
        childTree.props.className
      ),
      ...("disabled" in (childTree.props ?? {}) && disabled
        ? { disabled: true }
        : {}),
    });
  });

  const titleWithTooltip = (
    <div>
      <CORETooltip
        title={""}
        message={title}
        position={"topLeft"}
        hidden={!isOverflowed}
        className={classnames("width-full", "tooltip-container")}
        testID={testID}
      >
        <div ref={textElementRef} className={"titleWithTooltip"}>
          {title}
        </div>
      </CORETooltip>
    </div>
  );

  return (
    <TestIDWrapper
      testID={testID}
      className={classnames("core-card", {
        "fit-container-height": fitContainerHeight,
        "core-card--disabled": disabled,
      })}
    >
      <div
        className={classnames("core-card-inner", {
          "core-card-inner--linked": !!href && !disabled,
        })}
      >
        <Card
          {...prop}
          {...(layout === "vertical" && { cover: cover })}
          {...(title &&
            layout === "horizontal" && {
              title: titleWithTooltip,
            })}
          className={classnames(
            "core-card-container",
            layout,
            { "disable-hover": !hoverable },
            { [elevation]: elevation !== "none" },
            { lite: lite },
            { "core-card-container--disabled": disabled },
            className
          )}
          hoverable={!disabled && hoverable}
          actions={actionsWithStyle}
          bordered={bordered}
          aria-disabled={disabled}
          {...(!disabled && hoverable && { onClick: onClick })}
        >
          {layout === "vertical" ? (
            <>
              {title && (
                <>
                  {titleWithTooltip}
                  <COREDivider space={isSmall ? "sm" : "md"} />
                </>
              )}
              {children}
            </>
          ) : (
            <HorizontalCard
              headerMedia={headerMedia}
              disabled={disabled}
              onCarouselImageClick={onCarouselImageClick}
            >
              {children}
            </HorizontalCard>
          )}
        </Card>
        {href && !disabled && (
          <Link
            to={href}
            className={"core-card-stretched-link"}
            aria-label={typeof title === "string" ? title : undefined}
            tabIndex={-1}
            target={target}
            {...(target === "_blank" && { rel: "noopener noreferrer" })}
          />
        )}
      </div>
    </TestIDWrapper>
  );
};

export const CORECard = HOCErrorBoundary(CORECardNoError);
